import React from 'react';
import { connect, useSelector } from 'react-redux';
import { campaignsClient, productsClient } from 'apollo';
import { useQuery } from '@apollo/client';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import * as selectors from 'containers/Campaigns/CreateEdit/store/selectors';
import * as filtersSelectors from 'components/FilterModal/store/selectors';
import * as actions from 'containers/Campaigns/CreateEdit/store/actions';
import { DetailsType } from 'containers/Campaigns/CreateEdit/store/reducer';
import CreateCampaignBreadcrumb from 'components/Campaigns/CreateEdit/Breadcrumb';
import CreateEditCampaignDetails from 'containers/Campaigns/CreateEdit/Details';
import CreateCampaignConfirmation from 'components/Campaigns/CreateEdit/Confirmation';

import * as filterActions from 'components/FilterModal/store/actions';

import {
  clientIdSelector,
  currentBrandSelector,
  enterpriseBrandSelector,
} from 'store/global/selectors';

import {
  GetCampaignQuery as result,
  GetCampaignQueryVariables as vars,
  GetCampaignDocument as query,
} from 'generated/campaigns-graphql';

import {
  GetStoresQuery as StoresResult,
  GetStoresQueryVariables as StoresVars,
  GetStoresDocument as StoresQuery,
  GetProductsQuery as ProductsResult,
  GetProductsQueryVariables as ProductsVars,
  GetProductsDocument as ProductsQuery,
} from 'generated/products-graphql';

import LoadingSpinner from 'components/LoadingSpinner';
import CreateCampaignAction from 'containers/Campaigns/CreateEdit/CreateAction';
import UpdateCampaignAction from 'containers/Campaigns/CreateEdit/UpdateAction';
import { CAMPAIGNS_ITEMS_TYPES, FilterSelectionType } from 'utils/types';
import { ProductSelection } from 'components/ProductsSelection/types';
import BodyFilterModal from 'components/FilterModal';
import './styles.scss';

interface Props {
  campaignId?: string;
  currentStep: number;
  details: DetailsType;
  show: boolean;
  selection: ProductSelection;
  onCampaignSet(campaign: any): void;
  onClose(): void;
  onContinue(currentStep: number): void;
  onPrevious(currentStep: number): void;
  clearSelections: () => void;
  onResetModal: () => void;
  initFilterValues: (values: FilterSelectionType) => void;
}

const CreateEditCampaignModal: React.FC<Props> = ({
  campaignId,
  currentStep,
  details,
  show,
  selection,
  onCampaignSet,
  onClose,
  onContinue,
  onPrevious,
  clearSelections,
  onResetModal,
  initFilterValues,
}) => {
  const {
    data: campaignData,
    error,
    loading,
  } = useQuery<result, vars>(query, {
    client: campaignsClient as any,
    variables: { id: campaignId },
    skip: !campaignId,
    fetchPolicy: 'network-only',
    onCompleted: ({ retrieve }) => {
      const retrieveFilters = { ...retrieve.itemFilters, ...retrieve.storeFilters };

      const filters = {
        accounts: retrieveFilters.accounts ?? [],
        states: retrieveFilters.states ?? [],
        stores: retrieveFilters.stores ?? [],
        items: retrieveFilters.items ?? [],
      };
      initFilterValues(filters);
      onCampaignSet(retrieve);
    },
  });

  const clientId = useSelector(clientIdSelector);
  const currentBrand = useSelector(currentBrandSelector);
  const enterpriseBrand = useSelector(enterpriseBrandSelector);

  const storesParam = campaignData?.retrieve?.storeFilters?.stores;
  const itemsParam = campaignData?.retrieve?.itemFilters?.items;

  const { data: stores, loading: storesLoading } = useQuery<StoresResult, StoresVars>(StoresQuery, {
    client: productsClient as any,
    variables: {
      clientID: clientId,
      brand: enterpriseBrand ? currentBrand : '',
      stores: storesParam && storesParam.length ? storesParam : [],
      pageable: {
        page: 1,
        pageSize: storesParam && storesParam.length ? storesParam.length : 10,
      },
    },
    skip: !campaignId || (storesParam && storesParam.length === 0),
    fetchPolicy: 'network-only',
  });

  const { data: products, loading: productsLoading } = useQuery<ProductsResult, ProductsVars>(
    ProductsQuery,
    {
      client: productsClient as any,
      variables: {
        clientID: clientId,
        brand: enterpriseBrand ? currentBrand : '',
        products: itemsParam && itemsParam.length ? itemsParam : [],
        pageable: {
          page: 1,
          pageSize: itemsParam && itemsParam.length ? itemsParam.length : 10,
        },
      },
      skip: !campaignId || (itemsParam && itemsParam.length === 0),
      fetchPolicy: 'network-only',
    }
  );

  const storesFromCampaign = stores?.stores?.data ? stores?.stores?.data : [];
  const productsFromCampaign = products?.products?.data ? products?.products?.data : [];

  const renderBody = () => {
    if (campaignId !== null) {
      if (error && !loading)
        return <Alert variant="danger">{`Error retrieving campaign with id: ${campaignId}`}</Alert>;
      if (loading || storesLoading || productsLoading)
        return (
          <div className="retrieving-container">
            <LoadingSpinner message="Retrieving campaign details..." />
          </div>
        );
      return renderContent();
    } else {
      return renderContent();
    }
  };

  const renderContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <BodyFilterModal
            typeOfFilter="stores"
            id={campaignId}
            storesFromEntity={storesFromCampaign}
            productsFromEntity={productsFromCampaign}
          />
        );
      case 2:
        return (
          <BodyFilterModal
            typeOfFilter="items"
            id={campaignId}
            storesFromEntity={storesFromCampaign}
            productsFromEntity={productsFromCampaign}
          />
        );
      case 3:
        return <CreateEditCampaignDetails />;
      default:
        return null;
    }
  };

  const isContinueDisabled = () => {
    switch (currentStep) {
      case 1:
        return (
          selection[CAMPAIGNS_ITEMS_TYPES.ACCOUNTS] &&
          selection[CAMPAIGNS_ITEMS_TYPES.ACCOUNTS].length === 0 &&
          selection[CAMPAIGNS_ITEMS_TYPES.STATES] &&
          selection[CAMPAIGNS_ITEMS_TYPES.STATES].length === 0 &&
          selection[CAMPAIGNS_ITEMS_TYPES.STORES] &&
          selection[CAMPAIGNS_ITEMS_TYPES.STORES].length === 0
        );
      case 2:
        return (
          selection[CAMPAIGNS_ITEMS_TYPES.ITEMS] &&
          selection[CAMPAIGNS_ITEMS_TYPES.ITEMS].length === 0
        );
    }
    return false;
  };

  const bodyClassname = currentStep === 3 ? 'details' : 'selection-content';

  const handleClose = () => {
    onClose();
    clearSelections();
    onResetModal();
  };

  return currentStep < 4 ? (
    <Modal
      centered
      dialogClassName="create-edit-campaign-modal"
      show={show}
      size="lg"
      onHide={handleClose}
    >
      <Modal.Header closeButton>
        <div>
          <Modal.Title as="div" bsPrefix="title">
            {campaignId === null
              ? 'Create New Campaign'
              : `Edit Campaign${details.name !== '' ? ` - ${details.name}` : ''}`}
          </Modal.Title>
          <div className="breadcrumb-container">
            <CreateCampaignBreadcrumb currentStep={currentStep} />
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className={bodyClassname}>{renderBody()}</div>
      </Modal.Body>
      <Modal.Footer>
        <Button className="btn-cancel" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          className="btn-previous"
          disabled={currentStep === 1}
          onClick={() => onPrevious(currentStep)}
        >
          Previous
        </Button>
        {currentStep < 3 && (
          <Button
            className="btn-continue"
            disabled={isContinueDisabled()}
            onClick={() => onContinue(currentStep)}
          >
            Continue
          </Button>
        )}
        {currentStep === 3 && campaignId === null && <CreateCampaignAction />}
        {currentStep === 3 && campaignId !== null && <UpdateCampaignAction />}
      </Modal.Footer>
    </Modal>
  ) : (
    <Modal centered show={show} onHide={onClose}>
      <CreateCampaignConfirmation name={details.name} />
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  currentStep: selectors.currentStepSelector(state),
  campaignId: selectors.campaignIdSelector(state),
  details: selectors.detailsSelector(state),
  show: selectors.showSelector(state),
  selection: filtersSelectors.selectionSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
  onCampaignSet: (campaignData: any) => {
    dispatch(actions.setCampaign(campaignData));
  },
  onClose: () => {
    dispatch(actions.setShow(false));
  },
  onContinue: (currentStep) => {
    dispatch(actions.setCurrentStep(currentStep + 1));
  },
  onPrevious: (currentStep) => {
    dispatch(actions.previous(currentStep));
  },
  onResetModal: () => {
    dispatch(actions.reset());
  },
  clearSelections: () => {
    dispatch(filterActions.clearSelections());
  },
  initFilterValues: (values: FilterSelectionType) => {
    dispatch(filterActions.initValues(values));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateEditCampaignModal);
